import { createStore } from 'zustand';
import type {
  BulkActionControllerStats,
  BulkActionItemResult,
  BulkActionSession,
  CreateBulkActionSessionInput,
  UpdateBulkActionItemInput,
} from './bulk-action-reporting.types';

type Clock = () => number;

const getDefaultStats = (totalItems: number): BulkActionControllerStats => ({
  total: totalItems,
  pending: totalItems,
  succeeded: 0,
  failed: 0,
  skipped: 0,
});

const getResultCounts = (
  itemResults: BulkActionItemResult[],
): Omit<BulkActionControllerStats, 'total' | 'pending'> =>
  itemResults.reduce(
    (counts, item) => {
      if (item.status === 'success') {
        return { ...counts, succeeded: counts.succeeded + 1 };
      }

      if (item.status === 'error') {
        return { ...counts, failed: counts.failed + 1 };
      }

      if (item.status === 'skipped') {
        return { ...counts, skipped: counts.skipped + 1 };
      }

      // 'loading' items are not terminal — they count as pending
      return counts;
    },
    { succeeded: 0, failed: 0, skipped: 0 },
  );

const computeStats = (
  total: number,
  itemResults: BulkActionItemResult[],
): BulkActionControllerStats => {
  const counts = getResultCounts(itemResults);

  return {
    total,
    pending: Math.max(
      total - counts.succeeded - counts.failed - counts.skipped,
      0,
    ),
    ...counts,
  };
};

const upsertItemResult = (
  itemResults: BulkActionItemResult[],
  input: UpdateBulkActionItemInput,
  updatedAt: number,
): BulkActionItemResult[] => {
  const nextResult: BulkActionItemResult = { ...input, updatedAt };
  const index = itemResults.findIndex(
    (item) => item.sourceEntityId === input.sourceEntityId,
  );

  if (index === -1) {
    return [...itemResults, nextResult];
  }

  const nextItemResults = [...itemResults];
  nextItemResults[index] = nextResult;
  return nextItemResults;
};

export interface BulkActionReportingStoreState {
  session: BulkActionSession | undefined;
  createSession: (input: CreateBulkActionSessionInput) => BulkActionSession;
  updateTotalItems: (totalItems: number) => BulkActionSession;
  recordItemResult: (input: UpdateBulkActionItemInput) => BulkActionSession;
  completeSession: () => BulkActionSession;
  failSession: (failureReason?: string) => BulkActionSession;
  /**
   * Transitions a completed/failed session back to 'running' and resets all
   * error items to 'loading' so they can be retried. Returns the IDs of the
   * items that were reset.
   */
  resumeSessionForRetry: () => {
    session: BulkActionSession;
    retriedEntityIds: string[];
  };
  clearSession: () => void;
}

const getRequiredSession = (
  session: BulkActionSession | undefined,
): BulkActionSession => {
  if (!session) {
    throw new Error('No active bulk action session');
  }
  return session;
};

export const createBulkActionReportingStore = (
  getTimestamp: Clock = Date.now,
) =>
  createStore<BulkActionReportingStoreState>()((set, get) => ({
    session: undefined,

    createSession: (input) => {
      const timestamp = getTimestamp();
      const session: BulkActionSession = {
        operationType: input.operationType,
        domainEntityType: input.domainEntityType,
        status: 'running',
        startedAt: timestamp,
        updatedAt: timestamp,
        metadata: input.metadata,
        stats: getDefaultStats(input.totalItems ?? 0),
        itemResults: [],
        sourceEnvironment: input.sourceEnvironment,
        targetEnvironment: input.targetEnvironment,
      };

      set({ session });
      return session;
    },

    updateTotalItems: (totalItems) => {
      const existingSession = getRequiredSession(get().session);
      const timestamp = getTimestamp();
      const nextSession: BulkActionSession = {
        ...existingSession,
        updatedAt: timestamp,
        stats: computeStats(totalItems, existingSession.itemResults),
      };

      set({ session: nextSession });
      return nextSession;
    },

    recordItemResult: (input) => {
      const existingSession = getRequiredSession(get().session);
      const timestamp = getTimestamp();
      const itemResults = upsertItemResult(
        existingSession.itemResults,
        input,
        timestamp,
      );
      const nextSession: BulkActionSession = {
        ...existingSession,
        updatedAt: timestamp,
        itemResults,
        stats: computeStats(existingSession.stats.total, itemResults),
      };

      set({ session: nextSession });
      return nextSession;
    },

    completeSession: () => {
      const existingSession = getRequiredSession(get().session);
      const timestamp = getTimestamp();
      const nextSession: BulkActionSession = {
        ...existingSession,
        status: 'completed',
        updatedAt: timestamp,
        finishedAt: timestamp,
        stats: computeStats(
          existingSession.stats.total,
          existingSession.itemResults,
        ),
      };

      set({ session: nextSession });
      return nextSession;
    },

    failSession: (failureReason) => {
      const existingSession = getRequiredSession(get().session);
      const timestamp = getTimestamp();
      const nextSession: BulkActionSession = {
        ...existingSession,
        status: 'failed',
        failureReason,
        updatedAt: timestamp,
        finishedAt: timestamp,
        stats: computeStats(
          existingSession.stats.total,
          existingSession.itemResults,
        ),
      };

      set({ session: nextSession });
      return nextSession;
    },

    resumeSessionForRetry: () => {
      const existingSession = getRequiredSession(get().session);
      if (existingSession.status === 'running') {
        throw new Error('Cannot retry a session that is still running');
      }

      const hasErrorItems = existingSession.itemResults.some(
        (item) => item.status === 'error',
      );

      // No-op when there are no failed items — avoids incorrectly
      // transitioning a completed session back to running.
      if (!hasErrorItems) {
        return { session: existingSession, retriedEntityIds: [] };
      }

      const timestamp = getTimestamp();

      const retriedEntityIds: string[] = [];
      const itemResults = existingSession.itemResults.map((item) => {
        if (item.status === 'error') {
          retriedEntityIds.push(item.sourceEntityId);
          return {
            ...item,
            status: 'loading' as const,
            error: undefined,
            updatedAt: timestamp,
          };
        }
        return item;
      });

      const nextSession: BulkActionSession = {
        ...existingSession,
        status: 'running',
        updatedAt: timestamp,
        finishedAt: undefined,
        failureReason: undefined,
        itemResults,
        stats: computeStats(existingSession.stats.total, itemResults),
      };

      set({ session: nextSession });
      return { session: nextSession, retriedEntityIds };
    },

    clearSession: () => {
      set({ session: undefined });
    },
  }));

export type BulkActionReportingStore = ReturnType<
  typeof createBulkActionReportingStore
>;
